import { mergeMap, catchError, map, switchMap, debounceTime } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { EMPTY, of, concat } from 'rxjs';
import { ModalService } from 'ui-components/services/modal-service';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';

import { GenericErrorRetryContainer } from '../../containers/generic-error-retry-container';
import Global from '../../store/global-selectors';
import queryString from 'qs';
import * as Actions from './customers.actions';
import * as GridActions from './grid/customers.grid.actions';
import * as NavActions from './nav/customers.nav.actions';
import * as FilteringActions from './filtering/customers.filtering.actions';
import * as CustomerOrdersActions from './customer-orders-grid/customer-orders.grid.actions';
import { setSelectedCustomers } from './selected-customers/customers.selected-customers.actions';
import * as Api from './customers.api';
import { entitiesLoad } from '../../store/entities/entities.actions';
import { mapCriterionToApiParam } from '../../services/filtering/api-param-map-service';
import { ColumnOrderByParamService } from '../../services/column-orderby-param-service';
import { CustomerDetailsDrawer } from '../../components/customer-details-drawer';
import { CustomerMarketplaceUsernamesModal } from '../../components/customer-details-drawer/components/customer-marketplace-usernames-modal';
import { messages } from './messages';

const mapFilterToParamValue = (filter) => {
  const filterValues = [
    { key: 'state', value: 'FilterToStates' },
    { key: 'countryCode', value: 'FilterToCountryCode' },
    { key: 'marketplaces', value: 'FilterToMarketPlaces' },
    { key: 'tag', value: 'FilterToTagIds' },
  ];
  const currentFilter = filterValues.find((value) => filter.filterValue === value.key);
  if (currentFilter) {
    return {
      ...filter,
      filterValue: currentFilter.value,
    };
  }

  return filter;
};

const createHash = (dataArray, keyName) => {
  return dataArray.reduce(
    (acc, dataElement) => ({
      ...acc,
      [dataElement[keyName]]: dataElement,
    }),
    {},
  );
};

export const addTagsToCustomers = (action$) => {
  return action$.pipe(
    ofType(Actions.ADD_TAGS_TO_CUSTOMERS),
    mergeMap((action) => {
      const { customerIds, tagId } = action;

      return Api.addCustomerTag({ customerIds, tagId }).pipe(
        map(() => {
          return Actions.addTagsToCustomersSuccess(action);
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addTagsToCustomerErrorTitle} />,
              message: <FormattedMessage {...messages.addTagsToCustomerErrorMessage} />,
            },
          });
          return of(Actions.addTagsToCustomersFailure());
        }),
      );
    }),
  );
};

export const refreshCustomers = (action$) => {
  return action$.pipe(
    ofType(
      Actions.ADD_TAGS_TO_CUSTOMERS_SUCCESS,
      Actions.ADD_TAGS_TO_CUSTOMERS_FAILURE,
      Actions.REMOVE_TAGS_FROM_CUSTOMERS_SUCCESS,
      Actions.REMOVE_TAGS_FROM_CUSTOMERS_FAILURE,
      Actions.CREATE_CUSTOMER_SUCCESS,
      Actions.DELETE_CUSTOMER_SUCCESS,
      Actions.UPDATE_CUSTOMER_SUCCESS,
      GridActions.CUSTOMERS_GRID_CHANGE_ORDERBY,
    ),
    mergeMap(() => {
      return of(Actions.silentRequestGetCustomers());
    }),
  );
};

export const removeTagsFromCustomers = (action$) => {
  return action$.pipe(
    ofType(Actions.REMOVE_TAGS_FROM_CUSTOMERS),
    mergeMap((action) => {
      const { customerIds, tagId } = action;

      return Api.removeCustomerTag({ customerIds, tagId }).pipe(
        map(() => {
          return Actions.removeTagsFromCustomersSuccess(action);
        }),
        catchError(() => {
          ModalService.launchModal({
            component: GenericErrorRetryContainer,
            props: {
              action,
              title: <FormattedMessage {...messages.addTagsToCustomerErrorTitle} />,
              message: <FormattedMessage {...messages.addTagsToCustomerErrorMessage} />,
            },
          });
          return of(Actions.removeTagsFromCustomersFailure());
        }),
      );
    }),
  );
};

export const getCustomersRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GET_CUSTOMERS_REQUEST, Actions.GET_SILENT_CUSTOMERS_REQUEST),
    debounceTime(500),
    switchMap(() => {
      const state = getState();
      const hasCustomerManagementRole = Global.selectUserhasCustomerManagementRole(getState());
      if (!hasCustomerManagementRole) {
        return EMPTY;
      }
      const statusViewId = Global.selectCustomersViewId(state);
      const statePage = Global.selectCustomersGridPage(state);
      const statePageSize = Global.selectCustomersGridPageSize(state);
      const activeFilterView = Global.selectCustomersFilteringActiveFilterView(state);
      const activeFilterViewId = activeFilterView.filterViewId;
      const appliedFilters = activeFilterView.appliedFilters || [];
      const filtersAsApiParams = appliedFilters.map((filter) => {
        const mappedFilter = mapFilterToParamValue(filter);
        return mapCriterionToApiParam(mappedFilter);
      });
      const searchTerm = Global.selectCustomersGridSearchTerm(state);
      const orderByColumn = Global.selectCustomersGridCurrentOrderByColumn(state, { statusViewId, activeFilterViewId });
      const orderByParam = ColumnOrderByParamService.getCustomersGridOrderByApiParamOrDefault(orderByColumn);
      const extraParams = [...filtersAsApiParams];
      extraParams.push(orderByParam);

      return Api.getCustomers(searchTerm, statePage, statePageSize, extraParams).pipe(
        mergeMap((response) => {
          const { page, pageSize, totalCount, pageData } = response;

          const entitiesToLoad = {
            entities: {
              ...state.entities,
              customers: createHash(pageData[0].customers, 'customerId'),
            },
          };

          return concat(of(entitiesLoad(entitiesToLoad)), of(Actions.getCustomersSuccess(page, pageSize, totalCount, pageData[0])));
        }),
        catchError((error) => {
          return of(Actions.getCustomersFailure(error));
        }),
      );
    }),
  );
};

export const changeCustomersPage = (action$, { getState }, { push, history }) => {
  return action$.pipe(
    ofType(GridActions.CUSTOMERS_GRID_CHANGE_PAGE),
    mergeMap(() => {
      const state = getState();
      // These should be converted to use location.search instead of redux state
      const statePage = Global.selectCustomersGridPage(state);
      const statePageSize = Global.selectCustomersGridPageSize(state);
      const pathname = history.location.pathname;
      const search = queryString.stringify({
        page: statePage,
        pageSize: statePageSize,
      });
      push({ pathname, search });
      return concat(of(Actions.requestGetCustomers()));
    }),
  );
};

export const quickSearchForCustomers = (action$, { getState }) =>
  action$.pipe(
    ofType(NavActions.CUSTOMERS_SEARCH),
    mergeMap(() => {
      const state = getState();
      const searchData = Global.selectCustomersNavSearchData(state);

      const searchParams = {
        ...searchData,
        page: Global.selectCustomersGridPage(state),
        pageSize: Global.selectCustomersGridPageSize(state),
      };

      return Api.getCustomersFromQuickSearch(searchParams).pipe(
        mergeMap((response) => {
          const { page, pageSize, totalCount, pageData } = response;
          const customerEntities = {
            ...state.entities.customers,
            ...createHash(pageData[0].customers, 'customerId'),
          };
          const entitiesToLoad = {
            entities: {
              ...state.entities,
              customers: customerEntities,
            },
          };

          return concat(
            of(entitiesLoad(entitiesToLoad)),
            of(
              Actions.quickSearchCustomersSuccess({
                page,
                pageSize,
                totalCount,
                pageData: pageData[0],
                searchTerm: searchData.searchTerm,
              }),
            ),
          );
        }),
        catchError(() => EMPTY),
      );
    }),
  );

export const setCustomColumnConfigurationOnFilterUpdate = (action$, { getState }) => {
  return action$.pipe(
    ofType(FilteringActions.CUSTOMERS_AUTOSWITCH_FILTERVIEW_CUSTOM),
    mergeMap((action) => {
      const state = getState();
      const currentViewId = get(Global.selectCustomersViewView(state), 'id') || 'undefined';
      const previousFilterView = action.filterView;
      const previousfilterViewId = previousFilterView.filterViewId;
      const userViewConfigurations = Global.selectCustomersGridUserGridConfigurations(state);
      const customViewColumnConfiguration =
        get(userViewConfigurations, `${currentViewId}.${previousfilterViewId}`) || get(userViewConfigurations, `${currentViewId}.default`);
      return of(GridActions.updateCustomViewGridConfiguration(customViewColumnConfiguration));
    }),
  );
};

export const launchCustomerDetailModal = (action$) => {
  return action$.pipe(
    ofType(GridActions.LAUNCH_CUSTOMER_DETAIL_MODAL),
    mergeMap((action) => {
      const { customerId } = action;
      ModalService.launchModal({
        component: CustomerDetailsDrawer,
        props: { customerId },
      });
      return concat(of(CustomerOrdersActions.getPreviousOrders(customerId)), of(setSelectedCustomers([customerId])));
    }),
  );
};

export const getCustomerPreviousOrders = (action$) => {
  return action$.pipe(
    ofType(CustomerOrdersActions.GET_CUSTOMER_PREVIOUS_ORDERS),
    switchMap((action) => {
      const { customerId } = action;
      return Api.getCustomerPreviousOrders(customerId).pipe(
        map((response) => CustomerOrdersActions.customerPreviousOrdersSuccess(response)),
        catchError(() => EMPTY),
      ); // TODO: handle error case
    }),
  );
};

export const clearCustomersSearch = (action$) =>
  action$.pipe(
    ofType(NavActions.CUSTOMERS_SEARCH_CLEAR_INPUT),
    mergeMap(() => {
      return of(Actions.requestGetCustomers());
    }),
  );

export const launchCustomerMarketplacesModal = (action$) => {
  return action$.pipe(
    ofType(Actions.LAUNCH_CUSTOMER_MARKETPLACE_USERNAMES_MODAL),
    mergeMap(({ marketplaces }) => {
      ModalService.launchModal({
        component: CustomerMarketplaceUsernamesModal,
        props: { marketplaces },
      });
      return EMPTY;
    }),
  );
};
